import { ContentFilters, Widget } from 'src/app/core/models/core.models';
import { NomLanguages } from 'src/app/core/constants';

export type WidgetExportFormat = 'csv' | 'xlsx' | 'pdf' | 'png' | 'jpg';

/**
 * Everything POST `/widget/create` takes. `row` / `col` / `sizeX` / `sizeY` /
 * `placeholderWidgetId` are derived from the current view's widget list - see
 * `WidgetPersistenceService.createWidget`.
 */
export interface CreateWidgetRequest {
  chartId: number;
  chartTypeId: number;
  objectId: string;
  objectClassId: number;
  dataSourceObjectId: string;
  dataSourceObjectClassId: number;
  row: number;
  placeholderWidgetId?: string | null;
  col?: number | null;
  sizeX?: number | null;
  sizeY?: number | null;
  pageId?: number | null;
}

export const createWidget = {
  /**
   * Two quirks of this body are load-bearing:
   *
   * - **`page_id` is the literal string `"null"` for feeds**, which is what the
   *   backend has always received.
   * - **`col` / `sizeX` / `sizeY` / `placeholderWidgetId` are omitted when
   *   absent.** They come from the selected placeholder widget, so they are
   *   `undefined` whenever no placeholder is selected.
   */
  toMultipartFormData(request: CreateWidgetRequest): FormData {
    const formData = new FormData();

    formData.append('chart_id', String(request.chartId));
    formData.append('chart_type_id', String(request.chartTypeId));
    formData.append('object_id', String(request.objectId));
    formData.append('object_class_id', String(request.objectClassId));
    formData.append('row', String(request.row));
    formData.append(
      'data_source_object_id',
      String(request.dataSourceObjectId),
    );
    formData.append(
      'data_source_object_class_id',
      String(request.dataSourceObjectClassId),
    );
    formData.append(
      'page_id',
      request.pageId ? String(request.pageId) : 'null',
    );

    if (request.placeholderWidgetId != null) {
      formData.append('placeholderWidgetId', request.placeholderWidgetId);
    }
    if (request.col != null) {
      formData.append('col', String(request.col));
    }
    if (request.sizeX != null) {
      formData.append('sizeX', String(request.sizeX));
    }
    if (request.sizeY != null) {
      formData.append('sizeY', String(request.sizeY));
    }

    return formData;
  },
};

export const widgetsOrder = {
  /**
   * Body of PATCH `/widget/update_widgets_order` - urlencoded repeated
   * `objects[i][…]` keys. It builds a `FormData` first and copies it into
   * `URLSearchParams`, which is what stringifies an absent size field to
   * `"undefined"`; the backend accepts that.
   */
  toUrlEncoded(widgets: Widget[]): URLSearchParams {
    const formData = new FormData();

    widgets.forEach((widget, index) => {
      formData.append(`objects[${index}][widget_id]`, widget.id);
      formData.append(`objects[${index}][object_id]`, widget.object_id);
      formData.append(
        `objects[${index}][object_class_id]`,
        widget.object_class_id?.toString(),
      );
      formData.append(`objects[${index}][title]`, widget.title);
      formData.append(`objects[${index}][sizeX]`, widget?.sizeX?.toString());
      formData.append(`objects[${index}][sizeY]`, widget?.sizeY?.toString());
      formData.append(`objects[${index}][col]`, widget.col.toString());
      formData.append(`objects[${index}][row]`, widget.row.toString());
    });

    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncodedData.append(key, value.toString());
    });

    return urlEncodedData;
  },
};

export const widgetData = {
  /**
   * Multipart body shared by `/widget/:id/widget_data` and `/widget/:id/export`
   * (export only adds `export_format`).
   *
   * Content-Type is deliberately not set - HttpClient adds the multipart boundary
   * itself.
   */
  toMultipartFormData(
    contentFilters: ContentFilters,
    languageId: number,
    cacheEnabled: boolean,
    exportFormat?: WidgetExportFormat,
  ): FormData {
    const formData = new FormData();

    const languageString = languageId
      ? languageId.toString()
      : NomLanguages.ENGLISH.toString();
    formData.append('lid', languageString);
    formData.append('cache_enabled', cacheEnabled ? 'true' : 'false');

    if (exportFormat) {
      formData.append('export_format', exportFormat);
    }

    // --- Start Filter Logic ---
    Object.keys(contentFilters).forEach((key) => {
      const value = (contentFilters as any)[key];
      if (key === 'filters' && value && typeof value === 'object') {
        Object.keys(value).forEach((filterKey) => {
          const filterValue = value[filterKey];
          if (filterKey === 'filter_queries') {
            if (Array.isArray(filterValue)) {
              filterValue.forEach((query: string, index: number) => {
                formData.append(
                  `content_filters[filters][filter_queries][${index}]`,
                  String(query),
                );
              });
            }
            return;
          }

          const processGroup = (group: any, type: 'include' | 'exclude') => {
            if (!group) return;
            const items = Array.isArray(group) ? group : [group];
            items.forEach((val: any, index: number) => {
              if (
                val &&
                typeof val === 'object' &&
                'start' in val &&
                'end' in val
              ) {
                if (val.start != null && val.end != null) {
                  formData.append(
                    `content_filters[filters][${filterKey}][${type}][${index}][start]`,
                    String(val.start),
                  );
                  formData.append(
                    `content_filters[filters][${filterKey}][${type}][${index}][end]`,
                    String(val.end),
                  );
                }
              } else {
                formData.append(
                  `content_filters[filters][${filterKey}][${type}][${index}]`,
                  String(val),
                );
              }
            });
          };

          processGroup(filterValue?.include, 'include');
          processGroup(filterValue?.exclude, 'exclude');
        });
      } else if (value !== undefined && value !== null) {
        formData.append(`content_filters[${key}]`, value.toString());
      }
    });

    return formData;
  },
};
